import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Home, Wrench, Plus, CheckCircle2, Clock, AlertTriangle, MapPin } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_META = {
  new: { label: "New", cls: "bg-[#3a4a6b]/12 text-[#3a4a6b]", icon: Clock },
  in_progress: { label: "In Progress", cls: "bg-[#D98324]/12 text-[#D98324]", icon: Wrench },
  resolved: { label: "Resolved", cls: "bg-[#2B4C3F]/12 text-[#2B4C3F]", icon: CheckCircle2 },
};

export default function TenantHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Other", priority: "medium", photos: [] });

  const load = () => api.get("/my-tenancy").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const onPhoto = (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    Promise.all(files.map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); })))
      .then((arr) => setForm((prev) => ({ ...prev, photos: [...(prev.photos || []), ...arr] })));
  };

  const submit = async () => {
    try {
      await api.post("/issues", { ...form, property_id: data.property.id });
      toast.success("Issue reported — the team has been notified");
      setOpen(false);
      setForm({ title: "", description: "", category: "Other", priority: "medium", photos: [] });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="overline text-muted-foreground">Loading…</div>;
  const prop = data.property;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      <div>
        <p className="overline text-accent">Welcome, {user.name.split(" ")[0]}</p>
        <h1 className="font-head text-3xl lg:text-4xl font-extrabold tracking-tight mt-1">My Home</h1>
      </div>

      {!prop ? (
        <p className="p-10 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">No property is linked to your account yet. Please contact Bides.</p>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-[#18181A] text-white rounded-md p-6 h-fit">
            <Home className="h-6 w-6 text-[#C6A87C]" />
            <h2 className="font-head text-xl font-bold mt-3">{prop.title}</h2>
            <p className="text-sm text-white/70 flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{prop.address}</p>
            <div className="mt-4 pt-4 border-t border-white/15 text-sm space-y-1 text-white/80">
              <p>{prop.property_type} · {prop.bedrooms} bed · {prop.bathrooms} bath</p>
              {prop.council_tax_band && <p>Council tax band {prop.council_tax_band}</p>}
              {user.tenancy_start && <p>Tenancy from {fmtDate(user.tenancy_start)}</p>}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <span className="overline flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" />My Repair Reports</span>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button data-testid="report-issue-button"><Plus className="h-4 w-4 mr-1" />Report an Issue</Button></DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle className="font-head">Report a Repair</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label className="overline">What's the problem?</Label><Input data-testid="issue-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="e.g. Leaking kitchen tap" /></div>
                    <div><Label className="overline">Details</Label><Textarea data-testid="issue-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={4} /></div>
                    <div>
                      <Label className="overline">Photos (optional)</Label>
                      <Input data-testid="issue-photo-input" type="file" accept="image/*" multiple onChange={onPhoto} className="mt-1.5" />
                      {form.photos.length > 0 && <div className="flex gap-2 flex-wrap mt-2">{form.photos.map((src, i) => <img key={i} src={fileUrl(src)} alt="preview" className="h-20 w-20 rounded-md border border-border object-cover" />)}</div>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="overline">Category</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                          <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                          <SelectContent>{["Plumbing", "Electrical", "Heating", "Appliance", "Structural", "Pest", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="overline">Priority</Label>
                        <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                          <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                          <SelectContent>{["low", "medium", "high", "urgent"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter><Button data-testid="submit-issue-button" onClick={submit} disabled={!form.title || !form.description}>Submit Report</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {data.issues.length === 0 ? (
              <p className="p-10 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">No repair reports yet. Spotted something? Report it above.</p>
            ) : (
              <div className="space-y-3">
                {data.issues.map((iss) => {
                  const sm = STATUS_META[iss.status];
                  const Icon = sm.icon;
                  return (
                    <div key={iss.id} data-testid={`tenant-issue-${iss.id}`} className="bg-white border border-border rounded-md p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{iss.title}</p>
                            <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{iss.category}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{iss.description}</p>
                          {(iss.photos || (iss.photo ? [iss.photo] : [])).length > 0 && (
                            <div className="flex gap-2 flex-wrap mt-2" data-testid={`tenant-issue-photo-${iss.id}`}>
                              {(iss.photos || [iss.photo]).map((src, i) => <img key={i} src={fileUrl(src)} alt="issue" className="h-24 w-24 rounded-md border border-border object-cover" />)}
                            </div>
                          )}
                          {(iss.resolution_photos || []).length > 0 && (
                            <div className="mt-2"><p className="text-[0.6rem] overline text-[#2B4C3F]">After-fix</p>
                              <div className="flex gap-2 flex-wrap">{iss.resolution_photos.map((src, i) => <img key={i} src={fileUrl(src)} alt="after" className="h-24 w-24 rounded-md border border-[#2B4C3F]/30 object-cover" />)}</div>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">Reported {fmtDate(iss.created_at)}</p>
                          {iss.updates?.length > 0 && (
                            <div className="mt-2 pl-3 border-l-2 border-border space-y-1">
                              {iss.updates.map((u, i) => <p key={i} className="text-xs text-muted-foreground">{u.text} — {u.by}</p>)}
                            </div>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 whitespace-nowrap ${sm.cls}`}><Icon className="h-3 w-3" />{sm.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
